import { apiFetch, apiFetchRaw } from "../../lib/api-client";
import { toApiFailure } from "../../lib/errors";
import type { components } from "../../lib/api-types.gen";

/* Contract-derived types (ADR-S1-005). */
export type DraftForm = components["schemas"]["DraftForm"];
export type InputDraft = components["schemas"]["InputDraft"];

/**
 * The caller's own [NF-022] drafts. Plain functions rather than queries: the
 * autosave hook owns their timing, and a cached draft must never be offered
 * after it was saved or discarded.
 */

function draftPath(form: DraftForm, context: string): string {
  return context ? `/drafts/${form}?context=${encodeURIComponent(context)}` : `/drafts/${form}`;
}

/** The last autosaved state of one mask, or null when there is none. */
export async function fetchDraft(form: DraftForm, context: string): Promise<InputDraft | null> {
  try {
    return await apiFetch<InputDraft>(draftPath(form, context));
  } catch (error) {
    if (toApiFailure(error).status === 404) return null;
    throw error;
  }
}

/**
 * Autosave. `keepalive` lets the request outlive the page when it is being
 * closed or reloaded - the browser caps such a body at 64 KB, which a real
 * mask stays far below.
 */
export async function putDraft(
  form: DraftForm,
  context: string,
  payload: InputDraft["payload"],
  opts: { keepalive?: boolean } = {},
): Promise<void> {
  await apiFetchRaw(draftPath(form, context), {
    method: "PUT",
    body: JSON.stringify({ payload }),
    ...(opts.keepalive ? { keepalive: true } : {}),
  });
}

/** Discard ([AC-030]). Idempotent on the server. */
export async function deleteDraft(form: DraftForm, context: string): Promise<void> {
  await apiFetchRaw(draftPath(form, context), { method: "DELETE" });
}
